import { createSlice } from '@reduxjs/toolkit';

// LocalStorage cache functions
const CALENDAR_CACHE_KEY = (userId) => `calendar_cache_${userId}`;

function saveCalendarToLocalStorage(userId, data) {
  try {
    localStorage.setItem(CALENDAR_CACHE_KEY(userId), JSON.stringify(data));
  } catch (err) {
    /* Error handled */
  }
}

// Unified calendar data structure keyed by "YYYY-MM-DD"
// Each day entry may contain:
//   events: [{ date, title, user, contact, note, status }]
//     - user: user id of another app user; null for custom events
//     - contact: from contacts table (donor use case, later)
//   ovulationTests: [{ id, imageUrl, value, level, peak, takenAt }]
//     - only IPs add these, but structure is shared
//   ipData: { periodDay, peakPredicted, peakTest, ovulationActual, ovulationPrediction }
//     - periodDay: this is a period day
//     - peakPredicted: predicted ovulation day (client-side computed, CD14)
//     - peakTest: a peak test was detected on this day
//     - ovulationActual: actual ovulation detected from tests (value >= 0.65)
//     - ovulationPrediction: predicted ovulation day (CD14, persisted to DB)
//   sharedOvulationPredictions: [userId, ...]
//     - user IDs whose predicted ovulation falls on this day; profile data in separate Redux store
//   note: free-text note any user type can add

const calendarSlice = createSlice({
  name: 'calendar',
  initialState: {
    startMonth: null,
    endMonth: null,
    monthInView: null,
    calendarData: {},
    otherUsersCalendarData: {}, // Cache for other users' calendar data: { userId: { data, range_start, range_end, last_updated } }
  },
  reducers: {
    setVisibleMonths: (state, action) => {
      state.startMonth = action.payload.startMonth;
      state.endMonth = action.payload.endMonth;
    },
    setMonthInView: (state, action) => {
      state.monthInView = action.payload;
    },
    setCalendarData: (state, action) => {
      state.calendarData = action.payload;
      // Save to localStorage for persistence
      // Note: We need the current user ID, but we don't have access to it here
      // The loader/hook will handle localStorage saving
    },
    setOtherUsersCalendarData: (state, action) => {
      const { userId, data, range_start, range_end } = action.payload;
      state.otherUsersCalendarData[userId] = {
        data,
        range_start,
        range_end,
        last_updated: Date.now(),
      };
    },
    addOvulationTest: (state, action) => {
      const { test } = action.payload;
      // Convert UTC to local date string for key lookup
      const localDate = new Date(test.taken_at);
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;

      // Initialize day entry if it doesn't exist
      if (!state.calendarData[dateKey]) {
        state.calendarData[dateKey] = {};
      }

      // Initialize ovulationTests array if it doesn't exist
      if (!state.calendarData[dateKey].ovulationTests) {
        state.calendarData[dateKey].ovulationTests = [];
      }

      // Add the test to the array
      state.calendarData[dateKey].ovulationTests.push(test);

      // Update maxTestValue for this day
      if (test.value != null) {
        const currentMax = state.calendarData[dateKey].maxTestValue || 0;
        state.calendarData[dateKey].maxTestValue = Math.max(currentMax, test.value);
      }
    },
  },
});

export const { setVisibleMonths, setMonthInView, setCalendarData, setOtherUsersCalendarData, addOvulationTest } = calendarSlice.actions;
export default calendarSlice.reducer;
